import {
  ActionIcon,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Group,
  MultiSelect,
  Popover,
  Select,
  Stack,
  Switch,
  Table,
  TagsInput,
  Text,
  TextInput,
} from '@mantine/core';
import { IconLanguage, IconPlus, IconTrash } from '@tabler/icons-react';
import {
  ASK_TEXT_MAX,
  BUILT_IN_FUNCTIONS,
  LABEL_MAX,
  MAX_ACTIONS,
  MAX_SAFE_URLS,
  WIDGET_LOCALES,
  actionErrors,
  badSafeUrls,
  blankAction,
  cardActionTypes,
  setLocaleText,
  toProductCard,
  updateAgentUi,
} from '../../../helpers/agentUi';
import HelpPopover from '../../HelpPopover/HelpPopover';
import SettingRow from '../../SettingRow/SettingRow';
import { HELP } from './superAdminHelp';
import classes from './SuperAdminSections.module.css';

// An empty field is reported by Save, not while the row is still being filled in.
const shownError = (value, message) => (value === '' || value == null ? null : message);

// Texts of one action per language ({ <locale>: { text } }), in a popover.
const LocaleTexts = ({ name, title, map, max, onChange }) => {
  const entries = Object.entries(map || {});
  const missing = WIDGET_LOCALES.filter((locale) => !map?.[locale]);
  return (
    <Popover width={340} position="bottom-end" withArrow shadow="md">
      <Popover.Target>
        <Button
          size="compact-xs"
          variant="subtle"
          leftSection={<IconLanguage size={12} />}
          aria-label={`${title} of ${name} in every language`}
        >
          {entries.length}
        </Button>
      </Popover.Target>
      <Popover.Dropdown>
        <Text size="xs" fw={500} mb={4}>
          {title}
        </Text>
        <Stack gap={4}>
          {entries.map(([locale, entry]) => (
            <Group key={locale} gap={4} wrap="nowrap">
              <Text size="xs" w={20}>
                {locale}
              </Text>
              <TextInput
                size="xs"
                style={{ flex: 1 }}
                aria-label={`${title} ${locale}`}
                maxLength={max}
                value={entry.text}
                onChange={(e) => onChange(setLocaleText(map, locale, e.currentTarget.value))}
              />
              <ActionIcon
                variant="subtle"
                color="red"
                size="sm"
                aria-label={`Remove ${title} ${locale}`}
                onClick={() => onChange(setLocaleText(map, locale, null))}
              >
                <IconTrash size={12} />
              </ActionIcon>
            </Group>
          ))}
          {missing.length > 0 && (
            <Select
              size="xs"
              placeholder="Add a language"
              aria-label={`Add a language to ${title}`}
              data={missing}
              value={null}
              onChange={(locale) => locale && onChange(setLocaleText(map, locale, ''))}
            />
          )}
        </Stack>
        <Text size="xs" c="dimmed" mt="xs">
          A language without a text uses the widget's own wording.
        </Text>
      </Popover.Dropdown>
    </Popover>
  );
};

const ActionRow = ({ action, index, types, locale, onChange, onRemove }) => {
  const n = index + 1;
  const errors = actionErrors(action, types);
  const label = action.labels?.[locale];
  const update = (changes) => onChange({ ...action, ...changes });
  const isLink = action.do === 'openLink';
  const isTemplate = action.do === 'urlTemplate';
  const isAsk = action.do === 'ask';

  return (
    <Table.Tr>
      <Table.Td>
        <TextInput
          size="xs"
          w={110}
          aria-label={`Type ${n}`}
          placeholder="book"
          value={action.type}
          error={shownError(action.type, errors.type)}
          onChange={(e) => update({ type: e.currentTarget.value })}
        />
      </Table.Td>
      <Table.Td>
        <Autocomplete
          size="xs"
          w={120}
          aria-label={`Function ${n}`}
          data={BUILT_IN_FUNCTIONS}
          value={action.do}
          error={shownError(action.do, errors.do)}
          onChange={(fn) =>
            // Drop what the new function doesn't use: a hidden link would still be checked and sent.
            update({
              do: fn,
              ...(fn !== 'openLink' && { href: undefined }),
              ...(fn !== 'urlTemplate' && { template: undefined }),
              ...(fn !== 'ask' && { askText: undefined }),
            })
          }
        />
      </Table.Td>
      <Table.Td>
        {(isLink || isTemplate) && (
          <TextInput
            size="xs"
            miw={180}
            aria-label={isTemplate ? `Template ${n}` : `Link ${n}`}
            placeholder={isTemplate ? 'https://shop.example/book?date={date}' : 'From the agent'}
            value={(isTemplate ? action.template : action.href) || ''}
            error={isTemplate ? shownError(action.template, errors.template) : errors.href}
            onChange={(e) => update(isTemplate ? { template: e.currentTarget.value } : { href: e.currentTarget.value })}
          />
        )}
        {isAsk && (
          <Group gap={4} wrap="nowrap">
            <TextInput
              size="xs"
              miw={150}
              aria-label={`Ask text ${n}`}
              placeholder="Message it sends"
              maxLength={ASK_TEXT_MAX}
              value={action.askText?.[locale]?.text || ''}
              error={errors.askText}
              onChange={(e) =>
                update({ askText: setLocaleText(action.askText, locale, e.currentTarget.value || null) })
              }
            />
            <LocaleTexts
              name={action.type || n}
              title="Ask text"
              map={action.askText}
              max={ASK_TEXT_MAX}
              onChange={(askText) => update({ askText })}
            />
          </Group>
        )}
      </Table.Td>
      <Table.Td>
        <Group gap={4} wrap="nowrap">
          <TextInput
            size="xs"
            w={140}
            aria-label={`Label ${n}`}
            placeholder="Widget default"
            maxLength={LABEL_MAX}
            value={label?.text || ''}
            error={errors.labels}
            onChange={(e) => update({ labels: setLocaleText(action.labels, locale, e.currentTarget.value || null) })}
          />
          <LocaleTexts
            name={action.type || n}
            title="Label"
            map={action.labels}
            max={LABEL_MAX}
            onChange={(labels) => update({ labels })}
          />
        </Group>
      </Table.Td>
      {[
        ['onCards', 'On cards'],
        ['inReplies', 'In replies'],
      ].map(([flag, name]) => (
        <Table.Td key={flag} ta="center" data-label={name}>
          <Checkbox
            size="xs"
            display="inline-block"
            aria-label={`${name} ${n}`}
            checked={action[flag] === true}
            onChange={(e) => update({ [flag]: e.currentTarget.checked })}
          />
        </Table.Td>
      ))}
      <Table.Td>
        <ActionIcon variant="subtle" color="red" size="sm" aria-label={`Remove action ${n}`} onClick={onRemove}>
          <IconTrash size={14} />
        </ActionIcon>
      </Table.Td>
    </Table.Tr>
  );
};

/**
 * Super admin editor for chatBot.agentUi's actions, Safe URLs and card options. Shown only once the
 * API returns agentUi; with it null nothing is set, and the first change starts it from defaults.
 */
const ButtonsCardsSection = ({ chatBot, handleChange, domain }) => {
  const agentUi = chatBot.agentUi ?? null;
  const emit = (value) => handleChange({ target: { name: 'agentUi', value } });
  const set = (changes) => emit(updateAgentUi(agentUi, changes));

  const actions = agentUi?.actions ?? [];
  const safeUrls = agentUi?.safeUrls ?? [];
  const card = agentUi?.productCard ?? null;
  const locale = domain?.locale ? domain.locale.slice(0, 2).toLowerCase() : 'en';
  const types = actions.map((action) => action.type.trim());
  // Unique: two rows can share a type while editing, and MultiSelect throws on duplicates.
  const cardTypes = cardActionTypes(actions);
  const badHosts = badSafeUrls(safeUrls);

  const setActions = (next) => {
    // A card button whose action is gone or no longer on cards would be refused on save.
    const onCards = cardActionTypes(next);
    const buttons = (card?.buttons ?? []).filter((type) => onCards.includes(type));
    set({ actions: next, productCard: card ? toProductCard({ ...card, buttons }) : null });
  };
  const setCard = (changes) => set({ productCard: toProductCard({ ...(card ?? {}), ...changes }) });

  // Help examples add a row or a host; Undo puts back the whole previous agentUi.
  const withUndo = (apply) => (value) => {
    apply(value);
    return () => emit(agentUi);
  };

  return (
    <>
      <SettingRow
        label="Actions"
        top
        wide
        help={
          <HelpPopover
            {...HELP.actions}
            onUse={withUndo((example) => {
              const next = [...actions, example];
              // A location action is refused unless Shopper location is Ask.
              if (example.do === 'location')
                set({ actions: next, location: { ...(agentUi?.location ?? {}), mode: 'ask' } });
              else setActions(next);
            })}
          />
        }
      >
        {actions.length > 0 && (
          <Box className={`${classes.fold} ${classes.actions}`} data-fold="actions">
            <Table verticalSpacing={2} fz="xs">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Function</Table.Th>
                  <Table.Th>Link or text</Table.Th>
                  <Table.Th>Label ({locale})</Table.Th>
                  <Table.Th ta="center">On cards</Table.Th>
                  <Table.Th ta="center">In replies</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {actions.map((action, index) => (
                  <ActionRow
                    key={index}
                    action={action}
                    index={index}
                    types={types}
                    locale={locale}
                    onChange={(next) =>
                      setActions(actions.map((other, position) => (position === index ? next : other)))
                    }
                    onRemove={() => setActions(actions.filter((_, position) => position !== index))}
                  />
                ))}
              </Table.Tbody>
            </Table>
          </Box>
        )}
        <Group justify="space-between" mt={4}>
          <Button
            variant="subtle"
            size="compact-xs"
            leftSection={<IconPlus size={12} />}
            disabled={actions.length >= MAX_ACTIONS}
            onClick={() => setActions([...actions, blankAction()])}
          >
            Add action
          </Button>
          <Text size="xs" c="dimmed">
            {actions.length} of {MAX_ACTIONS} · clicks are always tracked
          </Text>
        </Group>
      </SettingRow>

      <SettingRow
        label="Safe URLs"
        htmlFor="agentUiSafeUrls"
        help={
          <HelpPopover
            {...HELP.safeUrls}
            onUse={withUndo((host) => set({ safeUrls: [...new Set([...safeUrls, host])] }))}
          />
        }
      >
        <TagsInput
          id="agentUiSafeUrls"
          size="xs"
          maw={640}
          placeholder="shop.example, *.shop.example"
          splitChars={[',', ' ']}
          maxTags={MAX_SAFE_URLS}
          value={safeUrls}
          error={badHosts.length ? `Not a hostname: ${badHosts.join(', ')}` : null}
          onChange={(values) => set({ safeUrls: values.map((value) => value.trim().toLowerCase()).filter(Boolean) })}
        />
      </SettingRow>

      <SettingRow label="Card options" top help={<HelpPopover {...HELP.productCard} />}>
        <Group gap="md">
          <Switch
            size="xs"
            label="Price on list cards"
            checked={card?.price === true}
            onChange={(e) => setCard({ price: e.currentTarget.checked })}
          />
          <Switch
            size="xs"
            label="Card fields"
            checked={card?.fields === true}
            onChange={(e) => setCard({ fields: e.currentTarget.checked })}
          />
        </Group>
        <Group gap="xs" mt="xs" align="flex-start" wrap="wrap">
          <MultiSelect
            size="xs"
            w={240}
            aria-label="Card buttons"
            placeholder={cardTypes.length ? 'Card buttons' : 'No action is on cards'}
            data={cardTypes}
            disabled={cardTypes.length === 0}
            value={card?.buttons ?? []}
            onChange={(buttons) => setCard({ buttons })}
          />
          <TextInput
            size="xs"
            w={280}
            aria-label="View cart link"
            placeholder="View cart link (optional)"
            value={card?.viewCartHref ?? ''}
            onChange={(e) => setCard({ viewCartHref: e.currentTarget.value })}
          />
        </Group>
      </SettingRow>

      {agentUi !== null && (
        <Group justify="flex-end" mt="xs">
          <Button variant="subtle" color="red" size="compact-xs" onClick={() => emit(null)}>
            Clear all agent UI settings
          </Button>
        </Group>
      )}
    </>
  );
};

export default ButtonsCardsSection;
